import { useFormikContext } from "formik";
import { Row, Col } from "antd";
import { Form, SubmitButton, Input } from "formik-antd";
import { useTranslation } from "react-i18next";
import DynamicButtons from "/app/src/components/generic/components/apps/data/settings/connectionSettings/dynamicButtons";

interface FormValues {
  value: string | undefined;
}

/**
 * Renders a form for submitting a request value with a customizable label.
 *
 * @param label - The label to display for the input field, provided as a JSX element.
 * @returns The request form component containing a text area input, dynamic buttons, and a submit button.
 *
 * @remarks
 * - Uses Formik context to access form state (`isSubmitting`, `dirty`).
 * - Utilizes Ant Design's `Form`, `Row`, `Col`, and `Input.TextArea` components for layout and input.
 * - Includes a `DynamicButtons` component for additional dynamic actions.
 * - The submit button is disabled unless the form is dirty and not submitting.
 * - The button label is internationalized using the `useTranslation` hook.
 */
export default function RequestForm({ label }: { label: JSX.Element }) {
  const { isSubmitting, dirty } = useFormikContext<FormValues>();
  const { t } = useTranslation();

  return (
    <Form layout="vertical">
      <Row gutter={16}>
        <Col span={20}>
          <Form.Item name="value" label={label} hasFeedback={false}>
            <Input.TextArea name="value" size="large" />
            <DynamicButtons />
          </Form.Item>
        </Col>
        <Col span={4}>
          <SubmitButton
            type="primary"
            size="large"
            block
            disabled={!dirty || isSubmitting}
            style={{ marginTop: "30px" }}
          >
            {t("translation:save")}
          </SubmitButton>
        </Col>
      </Row>
    </Form>
  );
}
